import { useParams } from 'react-router-dom';
import Loading from 'flow/components/ui-components/Loading';
import RouteLink from 'flow/components/ui-components/RouteLink';
import useAppContext from 'flow/hooks/useAppContext';
import useContact from 'flow/hooks/useContact';
import { Profile } from 'flow/types/Profile';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import { isEmptyNullOrUndefined } from 'flow/utils';
import { getCountryCode } from 'flow/utils/extension-utils';
import PrepareLandlineText from '../functions/prepareLandlineText';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { generatePath } from 'flow/utils/router-utils';

interface Props {
    profile: TypedMap<Profile>;
    allProfileKey?: string;
}

const Settings = ({ profile, allProfileKey }: Props) => {
    const { i18next } = useAppContext();
    const profileKey = allProfileKey || profile.get('key');

    const { extensionKey } = useParams<{ extensionKey: ExtensionKey }>();
    const { contact } = useContact(extensionKey);
    const countryCode = getCountryCode(contact, null);

    if (!countryCode) {
        return <Loading />;
    }

    const busyRedirectType = profile.getIn([
        'callForwarding',
        'busyRedirectNumber',
        'type',
    ]);

    const busyNumber =
        busyRedirectType === 'number' || busyRedirectType === 'sharedvoicemail'
            ? profile.getIn([
                  'callForwarding',
                  'busyRedirectNumber',
                  'forwardingNumber',
                  'nationalFormat',
              ])
            : null;

    const delayedRedirectType = profile.getIn([
        'callForwarding',
        'delayedRedirectNumber',
        'type',
    ]);

    const delayedNumber =
        delayedRedirectType === 'number' ||
        delayedRedirectType === 'sharedvoicemail'
            ? profile.getIn([
                  'callForwarding',
                  'delayedRedirectNumber',
                  'forwardingNumber',
                  'nationalFormat',
              ])
            : null;

    const disconnectedRedirectType = profile.getIn([
        'callForwarding',
        'errorRedirectNumber',
        'type',
    ]);

    const disconnectedNumber =
        disconnectedRedirectType === 'number' ||
        disconnectedRedirectType === 'sharedvoicemail'
            ? profile.getIn([
                  'callForwarding',
                  'errorRedirectNumber',
                  'forwardingNumber',
                  'nationalFormat',
              ])
            : null;

    const mex = profile.getIn(['parallelCalls', 'letThroughMexCalls']);
    const forwardingDisabled =
        !profile.get('editable') ||
        (profile.getIn(['callForwarding', 'unconditionalRedirect', 'type']) ===
            'voicemail' &&
            !mex);

    return (
        <>
            <RouteLink
                disabled={!profile.get('editable')}
                to={generatePath(
                    routes['settings.incoming-calls.profileKey.settingType'],
                    { extensionKey, profileKey, settingType: 'landline' },
                )}
                description={PrepareLandlineText({ profile, countryCode })}
            >
                {i18next.t('settings.incoming_calls.fixed')}
            </RouteLink>

            {!isEmptyNullOrUndefined(mex) && (
                <RouteLink
                    disabled={!profile.get('editable')}
                    to={generatePath(
                        routes[
                            'settings.incoming-calls.profileKey.settingType'
                        ],
                        { extensionKey, profileKey, settingType: 'mobile' },
                    )}
                    description={i18next.t(
                        profile.getIn(['parallelCalls', 'letThroughMexCalls'])
                            ? 'settings.incoming_calls.mex.mex'
                            : 'settings.incoming_calls.mex.fixed',
                    )}
                >
                    {i18next.t('settings.incoming_calls.mex')}
                </RouteLink>
            )}

            <RouteLink
                className="mt-6"
                disabled={forwardingDisabled}
                to={generatePath(
                    routes['settings.incoming-calls.profileKey.settingType'],
                    { extensionKey, profileKey, settingType: 'busy' },
                )}
                description={
                    busyNumber ||
                    i18next.t(
                        `settings.incoming_calls.busy.${busyRedirectType}`,
                    )
                }
            >
                {i18next.t('settings.incoming_calls.busy')}
            </RouteLink>

            <RouteLink
                disabled={forwardingDisabled}
                to={generatePath(
                    routes['settings.incoming-calls.profileKey.settingType'],
                    { extensionKey, profileKey, settingType: 'delay' },
                )}
                description={
                    delayedNumber ||
                    i18next.t(
                        `settings.incoming_calls.delayed.${delayedRedirectType}`,
                    )
                }
            >
                {i18next.t('settings.incoming_calls.delayed')}
            </RouteLink>

            <RouteLink
                disabled={forwardingDisabled}
                to={generatePath(
                    routes['settings.incoming-calls.profileKey.settingType'],
                    { extensionKey, profileKey, settingType: 'disconnected' },
                )}
                description={
                    disconnectedNumber ||
                    i18next.t(
                        `settings.incoming_calls.delayed.${disconnectedRedirectType}`,
                    )
                }
            >
                {i18next.t('settings.incoming_calls.disconnected')}
            </RouteLink>
        </>
    );
};

export default Settings;
